import { FC, useState, useEffect, useCallback, useMemo } from "react"
import {
  Calendar,
  Trash2,
  Loader2,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight
} from "lucide-react"
import { LoanOfficer } from "../../../../types"
import LoanOfficerAutocomplete from "../../loan-officer-autocomplete"
import { PrimaryBtn } from "../../buttons"
import Tabs from "../../tabs"
import calendlyApi, { CalendlyConfigResponse } from "../../../../api/calendly"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

type TabId = "new" | "existing"

const CalendlyConfig: FC = () => {
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState<TabId>("new")
  const [loid, setLoid] = useState<string>("")
  const [apiKey, setApiKey] = useState<string>("")
  const [schedulingUrl, setSchedulingUrl] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [integrations, setIntegrations] = useState<CalendlyConfigResponse[]>([])
  const [isLoadingList, setIsLoadingList] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 5

  const tabs = [
    { id: "new" as TabId, label: "New" },
    { id: "existing" as TabId, label: "Existing" }
  ]

  const filteredIntegrations = useMemo(() => {
    if (!searchQuery.trim()) return integrations
    const query = searchQuery.toLowerCase()
    return integrations.filter(
      i =>
        i.user.name.toLowerCase().includes(query) ||
        i.user.email.toLowerCase().includes(query)
    )
  }, [integrations, searchQuery])

  const totalPages = Math.ceil(filteredIntegrations.length / pageSize)
  const paginatedIntegrations = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredIntegrations.slice(start, start + pageSize)
  }, [filteredIntegrations, currentPage])

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  const fetchIntegrations = useCallback(async () => {
    try {
      const data = await calendlyApi.getAll()
      setIntegrations(data)
    } catch (error: any) {
      dispatch(
        pushToast({
          msg: "Failed to load Calendly integrations",
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoadingList(false)
    }
  }, [dispatch])

  useEffect(() => {
    fetchIntegrations()
  }, [fetchIntegrations])

  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this Calendly integration?"
      )
    ) {
      return
    }

    setDeletingId(id)
    try {
      await calendlyApi.delete(id)
      dispatch(
        pushToast({
          msg: "Calendly integration deleted",
          theme: "success",
          timeout: 3000
        })
      )
      fetchIntegrations()
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete Calendly integration"
      dispatch(
        pushToast({
          msg,
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setDeletingId(null)
    }
  }

  const canSubmit = loid && apiKey && schedulingUrl

  const handleSubmit = async () => {
    setIsLoading(true)

    try {
      await calendlyApi.configure({
        loid: parseInt(loid, 10),
        api_key: apiKey,
        scheduling_url: schedulingUrl
      })

      dispatch(
        pushToast({
          msg: "Calendly configuration saved successfully",
          theme: "success",
          timeout: 3000
        })
      )

      // Reset form and refetch list
      setLoid("")
      setApiKey("")
      setSchedulingUrl("")
      fetchIntegrations()
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save Calendly configuration"
      dispatch(
        pushToast({
          msg,
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoSelect = (lo: LoanOfficer) => {
    setLoid(lo.loid ?? "")
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Calendly Configuration
            </h3>
            <p className="text-sm text-gray-500">
              Configure Calendly integration for a loan officer
            </p>
          </div>
        </div>
      </div>

      <div className="px-6">
        <Tabs tabs={tabs} currentTab={activeTab} onChange={setActiveTab} />
      </div>

      {activeTab === "new" && (
        <div className="p-6 space-y-5">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loan Officer:
              </label>
              <LoanOfficerAutocomplete
                selected={loid}
                label=""
                direction="vertical"
                showBrandName
                showSearchToggle
                onChange={setLoid}
                onSelectLo={handleLoSelect}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key:
              </label>
              <input
                type="text"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter Calendly API key"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scheduling URL:
              </label>
              <input
                type="text"
                value={schedulingUrl}
                onChange={e => setSchedulingUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://calendly.com/username/event"
              />
            </div>
          </div>

          <div className="pt-2">
            <PrimaryBtn
              onClick={handleSubmit}
              disabled={!canSubmit || isLoading}
              className="w-full justify-center"
            >
              {isLoading ? "Saving..." : "Save Configuration"}
            </PrimaryBtn>
          </div>
        </div>
      )}

      {activeTab === "existing" && (
        <div className="p-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              onClick={() => {
                setIsLoadingList(true)
                fetchIntegrations()
              }}
              disabled={isLoadingList}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh list"
            >
              <RefreshCw
                className={`w-4 h-4 ${isLoadingList ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
          {isLoadingList ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          ) : integrations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <Calendar className="w-8 h-8 mb-2" />
              <p className="text-sm">No Calendly integrations configured</p>
            </div>
          ) : filteredIntegrations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <Search className="w-8 h-8 mb-2" />
              <p className="text-sm">No results found for "{searchQuery}"</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {paginatedIntegrations.map(integration => (
                  <div
                    key={integration._id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {integration.user.name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {integration.user.email}
                      </p>
                      <p className="text-xs text-gray-400 truncate mt-1">
                        {integration.user.scheduling_url}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(integration._id)}
                      disabled={deletingId === integration._id}
                      className="ml-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete integration"
                    >
                      {deletingId === integration._id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Showing {(currentPage - 1) * pageSize + 1}-
                    {Math.min(
                      currentPage * pageSize,
                      filteredIntegrations.length
                    )}{" "}
                    of {filteredIntegrations.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => p - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:hover:bg-transparent"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => p + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:hover:bg-transparent"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default CalendlyConfig
